/* ============================================================================
 * Movatic WMS — useDeviceMode
 * Arquivo: src/mobile/hooks/useDeviceMode.ts
 * Autor: CesarFilho 932048016
 * ----------------------------------------------------------------------------
 * Responsabilidade:
 *  - Informar se estamos no simulador (?sim=1), em “mobile real” (coletor/
 *    smartphone) ou em desktop.
 *  - Reagir a mudanças de URL, resize/orientation e display-mode (PWA).
 *  - Expor sinalizações auxiliares para o layout (ex.: showPhoneFrame/showTabs).
 *
 * Heurística “mobile real”:
 *  - userAgent móvel (inclui coletores) OU userAgentData.mobile
 *  - E (PWA standalone OU viewport pequena)
 *
 * Extras NÃO-quebradores:
 *  - Suporte opcional a ?mode=mobile|desktop (útil p/ testes); ?sim=1 continua
 *    tendo precedência para o “simulador”.
 *  - Leitura de visualViewport (quando houver) para medidas mais fiéis.
 *  - Debounce de eventos com requestAnimationFrame (evita re-renders excessivos).
 * ========================================================================== */

import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

export type DeviceMode = "simulator" | "mobile" | "desktop";

export type DeviceState = {
    simulator: boolean;
    realMobile: boolean;
    isStandalone: boolean;
    isMobileUA: boolean;
    mode: DeviceMode;
    viewport: { width: number; height: number; dpr: number };
    /** Mostrar moldura (PhoneFrame): apenas no simulador. */
    showPhoneFrame: boolean;
    /** Mostrar barra de abas inferior: escondida em mobile real. */
    showBottomTabs: boolean;
};

/** Obtém tamanho de viewport com fallback (SSR-safe). */
function readViewport() {
    if (typeof window === "undefined") {
        return { width: 1024, height: 768, dpr: 1 };
    }
    // visualViewport dá o tamanho realmente útil (desconta teclado etc.)
    const vv = (window as any).visualViewport as VisualViewport | undefined;
    const width = vv?.width ?? window.innerWidth;
    const height = vv?.height ?? window.innerHeight;
    const dpr = window.devicePixelRatio || 1;
    return { width, height, dpr };
}

/** Heurística de “UA móvel/coletores” incluindo userAgentData quando existir. */
function detectMobileUA(): boolean {
    if (typeof navigator === "undefined") return false;

    // Chromium modernos expõem userAgentData.mobile
    const anyNav = navigator as any;
    if (anyNav.userAgentData && typeof anyNav.userAgentData.mobile === "boolean") {
        if (anyNav.userAgentData.mobile) return true;
    }

    const ua = (navigator.userAgent || "").toLowerCase();

    // Smartphones/tablets comuns
    const common =
        /android|iphone|ipad|ipod|iemobile|opera mini|mobile/.test(ua);

    // Coletores e industriais comuns
    const rugged =
        /zebra|tc\d|ec\d|honeywell|datalogic|bluebird|unitech|cipherlab|keyence|m3/.test(
            ua
        );

    return common || rugged;
}

/** PWA standalone (Chrome/Android, iOS Safari). */
function detectStandalone(): boolean {
    const hasWindow = typeof window !== "undefined";
    const hasNavigator = typeof navigator !== "undefined";
    return (
        (hasWindow && window.matchMedia?.("(display-mode: standalone)").matches) ||
        (hasNavigator && (navigator as any).standalone === true) ||
        false
    );
}

/** Calcula estado a partir de `search` (sem efeitos colaterais). */
function computeDeviceState(search: string): DeviceState {
    const vp = readViewport();
    const params =
        typeof window !== "undefined"
            ? new URLSearchParams(search || window.location.search)
            : new URLSearchParams();

    // 1) modo simulador tem precedência
    const simulator = params.get("sim") === "1";

    // 2) override opcional via ?mode=mobile|desktop (não interfere no simulador)
    const modeOverride = (params.get("mode") || "").toLowerCase();
    const forcedMobile = modeOverride === "mobile";
    const forcedDesktop = modeOverride === "desktop";

    const isStandalone = detectStandalone();
    const isMobileUA = detectMobileUA();

    // limiar flexível: 840px cobre a maioria dos coletores/smartphones
    const smallViewport = vp.width < 840;

    // Heurística base
    let realMobile = isMobileUA && (isStandalone || smallViewport);

    // Override opcional
    if (forcedMobile) realMobile = true;
    if (forcedDesktop) realMobile = false;

    const mode: DeviceMode = simulator ? "simulator" : realMobile ? "mobile" : "desktop";

    return {
        simulator,
        realMobile,
        isStandalone,
        isMobileUA,
        mode,
        viewport: vp,
        showPhoneFrame: simulator,
        showBottomTabs: !realMobile,
    };
}

export function useDeviceMode(): DeviceState {
    const loc = useLocation();
    const initial = useMemo(() => computeDeviceState(loc.search), [loc.search]);
    const [state, setState] = useState<DeviceState>(initial);

    // Recalcula quando a URL muda (ex.: alternar ?sim=1 ou ?mode=mobile)
    useEffect(() => {
        setState(computeDeviceState(loc.search));
    }, [loc.search]);

    useEffect(() => {
        let raf = 0;
        const schedule = () => {
            if (raf) cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => setState(computeDeviceState(loc.search)));
        };

        // Resize/orientation mudam viewport => podem alterar heurística
        window.addEventListener("resize", schedule);
        window.addEventListener("orientationchange", schedule);

        // Mudança no display-mode (instalação PWA, abrir standalone)
        const dm = window.matchMedia?.("(display-mode: standalone)");
        const onDM = () => schedule();
        dm?.addEventListener?.("change", onDM);

        // iOS Safari não dispara o media query change sempre; como fallback,
        // atualiza após foco/retorno ao app.
        const visHandler = () => setTimeout(schedule, 100);
        document.addEventListener("visibilitychange", visHandler);

        // Alguns navegadores expõem visualViewport com eventos de resize/scroll
        const vv = (window as any).visualViewport as VisualViewport | undefined;
        vv?.addEventListener?.("resize", schedule);
        vv?.addEventListener?.("scroll", schedule);

        return () => {
            if (raf) cancelAnimationFrame(raf);
            window.removeEventListener("resize", schedule);
            window.removeEventListener("orientationchange", schedule);
            dm?.removeEventListener?.("change", onDM);
            document.removeEventListener("visibilitychange", visHandler);
            vv?.removeEventListener?.("resize", schedule);
            vv?.removeEventListener?.("scroll", schedule);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return state;
}
